/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import RovoIcon from '@atlaskit/icon-lab/core/rovo';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const styles = cssMap({
	wrapper: {
		position: 'relative',
		width: '100%',
		boxSizing: 'border-box',
		borderRadius: token('radius.small'),
		outlineWidth: token('border.width'),
		outlineStyle: 'solid',
		outlineColor: 'transparent',
		outlineOffset: '0',
		transition: 'outline-color 120ms ease',
		'&:hover': {
			outlineColor: token('color.border'),
		},
	},
	wrapperSelected: {
		outlineColor: token('color.border.focused'),
		'&:hover': {
			outlineColor: token('color.border.focused'),
		},
	},
	label: {
		position: 'absolute',
		top: token('space.negative.150'),
		right: token('space.100'),
		zIndex: 1,
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.050'),
		paddingTop: token('space.025'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.025'),
		paddingLeft: token('space.075'),
		borderRadius: token('radius.small'),
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		color: token('color.text.subtle'),
		font: token('font.body.small'),
		pointerEvents: 'none',
		userSelect: 'none',
		opacity: 0,
		transition: 'opacity 120ms ease, background-color 120ms ease, color 120ms ease',
	},
	labelVisible: {
		opacity: 1,
	},
	labelSelected: {
		opacity: 1,
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		borderColor: token('color.border.selected'),
	},
	body: {
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
	},
});


interface Props {
	/** Read-only body content rendered by the manifest renderer. */
	children: React.ReactNode;
	/** True when this block is the current PM selection (NodeSelection). */
	isSelected?: boolean;
	/** When true, renders only the body content without any interactive chrome (label, hover border). Used in view-only contexts such as the classic page renderer. */
	readOnly?: boolean;
}

/**
 * Custom chrome vs extension default: hover-/selection-revealed Rovo lozenge in the top-right,
 * subtle border on hover, focused border when selected, read-only body.
 */
export const AgentManagedExtensionView = ({ isSelected, children, readOnly }: Props): JSX.Element => {
	const intl = useIntl();
	const [isInteracted, setIsInteracted] = useState(false);
	const handleEnter = useCallback(() => setIsInteracted(true), []);
	const handleLeave = useCallback(() => setIsInteracted(false), []);

	if (readOnly) {
		// In view-only contexts (e.g. classic page renderer), render just the body
		// without any interactive chrome (hover border, label, selection state).
		return <React.Fragment>{children}</React.Fragment>;
	}

	const showLabel = isInteracted || isSelected;

	return (
		// eslint-disable-next-line @atlaskit/design-system/use-primitives
		<div
			css={[styles.wrapper, isSelected && styles.wrapperSelected]}
			onMouseEnter={handleEnter}
			onMouseLeave={handleLeave}
			onFocus={handleEnter}
			onBlur={handleLeave}
		>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives */}
			<span
				css={[
					styles.label,
					showLabel && styles.labelVisible,
					isSelected && styles.labelSelected,
				]}
			>
				<RovoIcon label="" size="small" />
				{intl.formatMessage(messages.blockLabel)}
			</span>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives */}
			<div css={styles.body}>{children}</div>
		</div>
	);
};
